<template>
    <b-modal
        no-fade
        ref="dialog"
        title-tag="h2"
        :title="modalTitle"
        lazy
        no-enforce-focus
        no-close-on-backdrop
        no-close-on-esc
        @show="$emit('show')"
        :scrollable="isScrollable"
    >
        <div v-if="currentView === 'CHECK_IN'">
            <b-alert :show="isShowLabItemWarning" variant="warning">
                Lab and radiology items are not selectable for the clinic's
                default provider.
            </b-alert>
            <b-form-group label-cols="3" label="Room:" label-for="room">
                <b-form-input
                    v-if="roomPrice"
                    plaintext
                    :value="`${room} (${totalNights} x ${_currency}${roomPrice}/night)`"
                />
                <b-form-input v-else plaintext :value="room" />
            </b-form-group>
            <b-form-group
                label-cols="3"
                label="Guest:"
                label-for="guest"
                class="center"
            >
                <router-link :to="`/patient/profile/${guestEncodedId}`">
                    {{ guest }}
                </router-link>
                {{ guestSpecies }} {{ guestWeightInfo }}
            </b-form-group>
            <b-form-group
                label-cols="3"
                label="Checking Out:"
                label-for="checkingOut"
            >
                <b-form-input plaintext :value="checkingOut" />
            </b-form-group>
            <b-form-group label-cols="3" label="Billing:" label-for="billing">
                <select class="form-control" v-model="invoice">
                    <option
                        v-for="inv in invoiceOptions"
                        :key="inv.id"
                        :value="inv.id"
                    >
                        {{ billingNote(inv) }}
                    </option>
                </select>
            </b-form-group>
            <b-form-group
                label-cols="3"
                label="Boarding Form:"
                label-for="boardingForm"
            >
                <v-select
                    id="bundle"
                    :options="boardingFormOptions"
                    v-model="boardingForm"
                    placeholder="Select template (optional)"
                    label="name"
                    class="show-actions"
                />
            </b-form-group>
            <b-form-group
                label-cols="3"
                label="Daily Rate:"
                label-for="dailyRate"
            >
                <vet-select
                    :disabled="isLoadingDailyRate"
                    module="rate-boarding"
                    placeholder="Daily rate (required)"
                    v-model="dailyRate"
                />
            </b-form-group>
            <b-form-group label-cols="3" label="Provider:" label-for="provider">
                <select
                    class="form-control"
                    v-model="provider"
                    :disabled="isLoadingProvider"
                >
                    <option value="" disabled>
                        Select provider (required)
                    </option>
                    <option
                        v-for="pro in providerOptions"
                        :key="pro.id"
                        :value="pro.id"
                    >
                        {{ pro.name }}
                    </option>
                </select>
            </b-form-group>
            <b-form-group
                label-cols="3"
                label="Medical Note:"
                label-for="medicalNote"
            >
                <select class="form-control" v-model="medicalNote">
                    <option
                        v-for="note in medicalNoteOptions"
                        :key="note.id"
                        :value="note.id"
                    >
                        {{ note.name }}
                    </option>
                </select>
            </b-form-group>
            <b-form-group label-cols="3" label="Bundle:" label-for="bundle">
                <v-select
                    id="bundle"
                    :options="bundleOptions"
                    v-model="bundle"
                    placeholder="Select bundle (optional)"
                    multiple
                    label="name"
                />
            </b-form-group>
            <b-form-group label-cols="3" label="Weight:" label-for="weight">
                <div class="weightWrapper">
                    <input
                        class="form-control"
                        v-model="weight"
                        placeholder="Weight (optional)"
                        step="any"
                        type="number"
                    />
                    <select class="form-control" v-model="weightUnit">
                        <option
                            v-for="weight in weightUnitOptions"
                            :key="weight.id"
                            :value="weight.id"
                        >
                            {{ weight.label }}
                        </option>
                    </select>
                </div>
            </b-form-group>
            <b-form-group
                label-cols="3"
                label="Cage Card:"
                label-for="cageCard"
            >
                <div class="printWrapper">
                    <select v-model="cageCard" class="form-control">
                        <option value="">Do not print cage card</option>
                        <option
                            v-for="printer in printers"
                            :key="printer.id"
                            :value="printer.id"
                        >
                            {{ printer.name }}
                        </option>
                    </select>
                    <div class="times">×</div>
                    <input
                        type="number"
                        placeholder="Quantity"
                        class="form-control"
                        v-model="printQuantity"
                        :disabled="
                            cageCard === '' ||
                            cageCard === 'no' ||
                            cageCard === 'local'
                        "
                    />
                </div>
            </b-form-group>
            <b-form-group label-cols="3" label="Notes:" label-for="notes">
                <textarea
                    id="notes"
                    v-model="notes"
                    class="form-control"
                    placeholder="Notes (optional)"
                    rows="3"
                ></textarea>
            </b-form-group>
        </div>
        <bundle
            v-else-if="currentView === 'BUNDLE_ITEM'"
            :app-data="bundleAppData"
            :data="bundleData"
            @loading="loadingBundle = true"
            @loaded="loadingBundle = false"
        />
        <template v-if="isSaving || loadingBundle" v-slot:modal-footer>
            <loader :margin="1" />
        </template>
        <template v-else v-slot:modal-footer="{ cancel }">
            <b-button @click="cancel">Cancel</b-button>
            <div class="gap"></div>
            <b-button
                v-if="currentView === 'CHECK_IN' && bundle.length > 0"
                variant="primary"
                @click="nextBundle"
                :disabled="isDisabledButtonSaveNext"
            >
                Next
            </b-button>
            <b-button
                v-if="currentView === 'BUNDLE_ITEM'"
                variant="primary"
                @click="backCheckIn"
            >
                Back
            </b-button>
            <b-button
                v-if="isShowSaveButton"
                variant="primary"
                @click="save"
                :disabled="isDisabledButtonSaveNext"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import vSelect from "vue-select";
import TemplateService from "@/services/templates.js";
import BoardingService from "@/services/boarding.js";
import StaffService from "@/services/staff.js";
import Loader from "@/module/commons/loader";
import Bundle from "@/module/commons/bundle/bundle";
import VetSelect from "@/module/commons/vet-select";
import ClientBillingsService from "@/services/client-billings";
import { isNull } from "lodash";
import LabMixin from "@/mixins/lab";

const CHECK_IN = "CHECK_IN";
const BUNDLE_ITEM = "BUNDLE_ITEM";

export default {
    components: {
        vSelect,
        Loader,
        Bundle,
        VetSelect
    },
    mixins: [LabMixin],
    data() {
        return {
            modalTitle: "Check In Reservation",
            currentView: CHECK_IN,
            boardingId: null,
            clientId: null,
            isSaving: false,
            room: "",
            roomId: null,
            guest: "",
            guestId: null,
            guestEncodedId: "",
            guestSpecies: null,
            guestWeightInfo: null,
            checkingOut: "",
            checkOutUnix: null,
            deposit: "",
            paymentType: "",
            invoice: "",
            boardingForm: null,
            provider: "",
            medicalNote: "",
            bundle: [],
            weight: "",
            weightUnit: "",
            cageCard: "",
            notes: "",
            printers: [],
            bundleOptions: [],
            invoiceOptions: [],
            medicalNoteOptions: [],
            providerOptions: [],
            boardingFormOptions: [],
            loadingBundle: false,
            isScrollable: false,
            rateItem: null,
            bundleData: {
                bundle_id: [],
                elements: [],
                selectedElements: []
            },
            bundleAppData: {
                isDefaultProvider: false
            },
            roomPrice: 0,
            priceItemId: null,
            printQuantity: 1,
            dailyRateOptions: [],
            dailyRate: null,
            isLoadingDailyRate: true,
            isLoadingProvider: true,
            totalNights: 0
        };
    },
    computed: {
        isDisabledButtonSaveNext() {
            if (!this.dailyRate || !this.provider || this.provider === "") {
                return true;
            }
            return false;
        },
        isShowLabItemWarning() {
            let pro = this.providerOptions.find(p => p.id === this.provider);
            if (
                pro &&
                pro.is_default_provider &&
                this.bundleData.bundle_id.length > 0
            ) {
                return true;
            }
            return false;
        },
        isShowSaveButton() {
            if (this.currentView === CHECK_IN && this.bundle.length > 0) {
                return false;
            }
            return true;
        },
        weightUnitOptions() {
            let raw = this.vitalweight_resource;
            let unitArr = [];
            for (const key in raw) {
                unitArr.push({ id: key, label: raw[key] });
            }
            return unitArr;
        }
    },
    watch: {
        cageCard(value) {
            // pdf
            if (value === "local") {
                this.printQuantity = 1;
            }
        },
        bundle(value) {
            this.bundleData.bundle_id = value;
        },
        dailyRate(value) {
            if (!value) {
                return;
            }
            let price = Number(value.price);
            if (isNaN(price)) {
                price = 0;
            }

            this.priceItemId = value.id;
            this.roomPrice = price.toFixed(2);
        },
        provider(id) {
            let pvdr = this.providerOptions.find(p => p.id === id);
            if (pvdr && pvdr.is_default_provider) {
                this.bundleAppData.isDefaultProvider = true;
            } else {
                this.bundleAppData.isDefaultProvider = false;
            }
        }
    },
    methods: {
        backCheckIn() {
            this.currentView = CHECK_IN;
            this.modalTitle = "Check In Reservation";
            this.isScrollable = false;
        },
        nextBundle() {
            this.currentView = BUNDLE_ITEM;
            this.modalTitle = "New Bundle";
            this.isScrollable = true;
        },
        async open(id) {
            if (this.$root.setting.weights_measures === 1) {
                this.weightUnit = 75;
            } else {
                this.weightUnit = 72;
            }

            let boardingData = await BoardingService.getBoardingById(id);
            let {
                room,
                patient,
                client,
                start,
                end,
                staff_id,
                note
            } = boardingData;
            this.boardingId = id;
            this.clientId = client.id;
            this.checkOutUnix = end;
            this.notes = note;

            // total nights
            this.totalNights = this.getTotalNights(start, end);

            // room
            this.room = room.name;
            this.roomId = room.id;

            // guest
            this.guestId = patient.id;
            this.guestEncodedId = patient.encoded_id;
            let guestName = patient.name;
            if (client) {
                guestName += ` ${client.lastname}`;
            }
            this.guest = guestName;

            // guest species
            let species = [];
            if (patient.patient_breeds) {
                species = patient.patient_breeds.map(s => s.species_name);
            }
            species = species.filter(
                (item, index) => species.indexOf(item) === index // remove duplicates
            );
            if (species.length > 0) {
                this.guestSpecies = "(" + species.join(", ") + ")";
            }

            // guest weight
            if (patient?.weight && patient?.weight_data?.date?.date) {
                let weightCreatedAt = this._carbonDateDisplay(
                    patient.weight_data.date
                );
                this.guestWeightInfo = `${patient.weight} (${weightCreatedAt})`;
            }

            // checkout
            this.checkingOut = moment
                .unix(end)
                .tz(this._timeZone)
                .format(this.getDateFormat() + ", hh:mm A");

            await this.initRoomFee(room.id);
            await this.initBundles();
            await this.initPrinters();
            await this.initInvoices(client.id);
            await this.initMedicalNote();
            await this.initProvider(staff_id);
            await this.initBoardingForm();

            this.currentView = CHECK_IN;
            this.$refs.dialog.show();
        },
        async initRoomFee(roomId) {
            this.isLoadingDailyRate = true;
            let res = await BoardingService.getRoomDetails(roomId);
            this.isLoadingDailyRate = false;

            if (res.room_fees && res.room_fees[0]) {
                let rate = res.room_fees[0];
                this.priceItemId = rate.id;
                this.roomPrice = rate.price;

                this.dailyRate = {
                    label: rate.display_name,
                    id: rate.id,
                    price: rate.price
                };
            }
        },
        async initBoardingForm() {
            let settings = await BoardingService.getSettings();
            let defaultLetterId = settings?.resource?.letter_id ?? null;

            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}account/template/letter`
            );
            let response = res.data.response;
            this.boardingFormOptions = response.resources || [];

            // set default boarding form if any
            if (defaultLetterId && settings.boarding_form) {
                let letterName = settings.boarding_form[defaultLetterId];
                this.boardingForm = {
                    id: defaultLetterId,
                    name: letterName
                };
            }
        },
        async initProvider(defaultId) {
            this.isLoadingProvider = true;
            let res = await StaffService.getProviders();
            let options = [];

            if (res.resource && res.resource.data) {
                let rawProviders = res.resource.data;

                for (let provider of rawProviders) {
                    if (!provider.actor) {
                        continue;
                    }

                    let { id, label, display_name: name } = provider.actor;

                    options.push({
                        id: id,
                        name: label || name,
                        is_default_provider: provider.is_default_provider || false // prettier-ignore
                    });
                }
            }

            let theProvider = options.find(p => p.id === defaultId);
            if (theProvider) {
                this.provider = theProvider.id;
            } else {
                this.provider = "";
            }

            this.providerOptions = options;
            this.isLoadingProvider = false;
        },
        async initMedicalNote() {
            let res = await TemplateService.getSoap();
            let options = res.resources || [];

            this.medicalNoteOptions = [
                { id: "", name: "Do not create medical note" },
                ...options
            ];
        },
        async initInvoices(clientId) {
            let options = [];
            let newOption = { id: "", name: "New Invoice" };

            try {
                let res = await ClientBillingsService.getInvoices(clientId);

                if (res && res.resource) {
                    options = res.resource;
                }
                options.push(newOption);
                this.invoiceOptions = options;
                this.invoice = options?.[0]?.id ?? ""; // get the first option, or else, new invoice
            } catch (error) {
                console.error(error);
                options = [newOption];
                this.invoice = ""; // default to new invoice
            }
        },
        async initBundles() {
            let res = await TemplateService.getBundles();
            this.bundleOptions = res.resources || [];
        },
        async initPrinters() {
            let response = await this.$http.get(
                process.env.ACCOUNT_SERVICE + "printer"
            );
            if (response) {
                this.printers = response.data.response.resources;
            }
        },
        async save() {
            try {
                this.isSaving = true;

                let soapId = null;
                if (this.medicalNote && this.medicalNote !== "") {
                    soapId = this.medicalNote;
                }

                let bundleIds = [];
                for (let bundle of this.bundle) {
                    bundleIds.push(bundle.id);
                }

                let bundleItems = [];
                for (let item of this.bundleData.selectedElements) {
                    let minQty = Number(item.quantity);
                    let defaultQty = Number(item.originalQuantity);
                    let sendToLab = 0;

                    if (item.send_to_lab && item.send_to_lab_value) {
                        sendToLab = 1;
                    }

                    if (isNaN(minQty)) {
                        minQty = 0;
                    }

                    if (isNaN(defaultQty)) {
                        defaultQty = 0;
                    }

                    bundleItems.push({
                        id: item.id,
                        min_qty: minQty,
                        default_qty: defaultQty,
                        send_to_lab: sendToLab,
                        tag_number: item.tag_number || null
                    });
                }

                let data = {
                    id: this.boardingId, // this 'id' is required for the backend to properly handle room capacity checking
                    boarding_id: this.boardingId,
                    patient_id: this.guestId,
                    client_id: this.clientId,
                    invoice_id: this.invoice,
                    item_id: this.priceItemId,
                    letter_id: this.boardingForm ? this.boardingForm.id : null,
                    checkout: this.checkOutUnix,
                    soap_id: soapId,
                    bundle_id: bundleIds,
                    items: bundleItems,
                    weight: this.weight,
                    weight_type: this.weightUnit,
                    print_card: this.cageCard === "" ? null : this.cageCard,
                    print_quantity: this.printQuantity || 0,
                    notes: this.notes,
                    provider_id: this.provider
                };

                let resultCheckIn = await BoardingService.saveCheckIn(data);

                if (resultCheckIn.response.code === 200) {
                    let resource = resultCheckIn.response.resources;

                    if (
                        resource &&
                        resource.antech_token &&
                        resource.accession_id
                    ) {
                        this.$root.$emit("call-antech-iframe", {
                            antech_token: resource.antech_token,
                            accession_id: resource.accession_id
                        });
                    }

                    if (data.print_card !== null) {
                        this.printCageCard({
                            showMessageCheckIn: true,
                            clientId: data.client_id,
                            patientId: data.patient_id,
                            printer: data.print_card,
                            quantity: data.print_quantity
                        });
                    } else {
                        this.$refs.dialog.hide();
                        this.$emit("save-success", this.boardingId);
                        this._message("Reservation successfully checked in");
                    }
                } else {
                    throw new Error(
                        resultCheckIn?.response?.message ??
                            "Error: Unable to check-in."
                    );
                }
            } catch (error) {
                this.isSaving = false;
                this._message(error.message, "danger");
            }
        },
        /**
         * printCageCard:
         * This method is similar (and modified) to /module/schedule/helper.js > _printCageCard
         * why? we dont want to include the helper as mixin into this file.
         * but why? the helper is designed for the whole schedule module. we just need this one function
         */
        async printCageCard({
            clientId,
            patientId,
            printer,
            quantity,
            showMessageCheckIn = false
        }) {
            if (!printer || printer == "no" || printer === "") {
                return Promise.resolve(true);
            }

            if (showMessageCheckIn) {
                this._message(
                    "Reservation successfully checked in.<br/>Printing cage card."
                );
            }

            try {
                let url = `${process.env.APPS}index.php/patient/cage_card/print_cage_card`;
                let params = {
                    client_id: clientId,
                    patient_id: patientId,
                    printer: printer,
                    print_quantity: quantity
                };
                let res = await this.$http.get(url, { params });

                this.$refs.dialog.hide();
                this.$emit("save-success");

                if (res.data.pdf != null) {
                    this.$root.$emit("bv::hide::modal");
                    this._redirect(res.data.pdf, true, 500);
                    return;
                }

                if (res.data.cage_card) {
                    let cageCardXML = res.data.cage_card;
                    let label = dymo.label.framework.openLabelXml(cageCardXML);
                    let printers = dymo.label.framework.getPrinters();
                    let printParams = dymo.label.framework.createLabelWriterPrintParamsXml(
                        {
                            copies: quantity
                        }
                    );

                    if (printers.length === 0) {
                        throw new Error("No DYMO printers are installed. Install DYMO printers."); // prettier-ignore
                    }

                    let printerName = "";
                    for (let i = 0; i < printers.length; ++i) {
                        let printer = printers[i];
                        if (printer.printerType == "LabelWriterPrinter") {
                            printerName = printer.name;
                            break;
                        }
                    }

                    if (printerName === "") {
                        throw new Error("No LabelWriter printers found. Install LabelWriter printer."); // prettier-ignore
                    }

                    // finally print the label
                    label.print(printerName, printParams);

                    if (res.data.message) {
                        this._message(res.data.message, "success");
                    }
                } else {
                    if (res.data.message) {
                        if (res.data.state && res.data.state == 0) {
                            this._message(res.data.message, "success");
                        } else {
                            this._message(res.data.message, null);
                        }
                    }
                }
            } catch (e) {
                console.log(e);
                this._message(e.message, "danger");
            }
        },
        getTotalNights(start, end) {
            let startdate = moment.unix(start).tz(this._timeZone);
            let endate = moment.unix(end).tz(this._timeZone);
            let totalnight = endate
                .clone()
                .startOf("day")
                .diff(startdate.clone().startOf("day"), "days");

            return totalnight;
        },
        billingNote(inv) {
            if (inv && inv.id) {
                if (isNull(inv.amount)) {
                    return `${inv.name}: - (${this._carbonDateDisplay(
                        inv.start
                    )})`;
                } else if (isNull(inv.start)) {
                    return `${inv.name}: ${this._toCurrency(inv.amount)} (--)`;
                } else if (isNull(inv.amount && inv.start)) {
                    return `${inv.name}: - (--)`;
                } else {
                    return `${inv.name}: ${this._toCurrency(
                        inv.amount
                    )} (${this._carbonDateDisplay(inv.start)})`;
                }
            } else {
                return `${inv.name}`;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.weightWrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 10px;
}
.printWrapper {
    display: grid;
    grid-template-columns: 2fr 20px 1fr;
    column-gap: 7px;
    align-items: center;

    .times {
        text-align: center;
    }
}
.center {
    display: flex;
    align-items: center;
}
::v-deep .modal-footer {
    display: flex;

    .gap {
        flex: 1;
    }
}
</style>
